import CloseIcon from "@mui/icons-material/Close"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"

/** Fill for candidate private tags (matches palette `colors.brand200`). */
export const CANDIDATE_TAG_FILL = "#EFD6FE"

const TAG_HEIGHT = 26
/** Horizontal depth of the left chevron (flat right edge of tag body stays at 100%). */
const CHEVRON_W = 18
/** Padding from bounding box so label clears the diagonal (not vertically “thin” at center). */
const LABEL_INSET = CHEVRON_W + 8
/** “Hole” sits in the wide part of the left wedge (inside polygon). */
const HOLE_LEFT = Math.round(CHEVRON_W * 0.5)

/**
 * Left-pointing price-tag / bookmark shape: chevron + flat rectangular body (no pill corners).
 */
export default function CandidatePriceTagChip({
  label,
  title: titleProp,
  onDelete,
  disabled = false,
  maxWidth,
}) {
  const title =
    titleProp !== undefined
      ? titleProp
      : typeof label === "string"
      ? label
      : undefined

  const clipPath = `polygon(0 50%, ${CHEVRON_W}px 0, 100% 0, 100% 100%, ${CHEVRON_W}px 100%)`

  return (
    <Box
      sx={{
        display: "inline-flex",
        maxWidth: maxWidth ?? "none",
        verticalAlign: "middle",
        filter: "drop-shadow(0 0 0.5px rgba(10, 37, 64, 0.12))",
      }}
    >
      <Box
        title={title}
        sx={{
          position: "relative",
          display: "inline-flex",
          flexDirection: "row",
          alignItems: "center",
          height: TAG_HEIGHT,
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          bgcolor: CANDIDATE_TAG_FILL,
          color: "text.primary",
          clipPath,
          pl: `${LABEL_INSET}px`,
          pr: onDelete ? 0.25 : 1.25,
          borderRadius: 0,
          "&::before": {
            content: '""',
            position: "absolute",
            left: HOLE_LEFT,
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: "#fff",
            boxShadow: "0 0 0 1px rgba(10, 37, 64, 0.08)",
            zIndex: 1,
            pointerEvents: "none",
          },
        }}
      >
        <Typography
          variant="caption"
          component="span"
          sx={{
            position: "relative",
            zIndex: 2,
            fontWeight: 600,
            lineHeight: 1,
            fontSize: "0.75rem",
            letterSpacing: "0.01em",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {label}
        </Typography>
        {onDelete ? (
          <IconButton
            type="button"
            size="small"
            aria-label={`Remove tag ${label}`}
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            disabled={disabled}
            sx={{
              position: "relative",
              zIndex: 2,
              p: 0.25,
              ml: 0.25,
              mr: 0.125,
              color: "text.secondary",
              "&:hover": { bgcolor: "rgba(10, 37, 64, 0.08)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  )
}
